import { useEffect, useState } from 'react';

import { CrosshairIcon, LocateFixedIcon, PanelLeftIcon, WebhookIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router';

import { FlowChartIcon } from '@/components/atoms/Icons/FlowChartIcon';
import { Button } from '@/components/ui/button';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { cn } from '@/lib/utils';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { Icon as IconProps } from '@/shared/types/icon';

import styles from './NavigationSidebar.module.scss';

type NavItem = { id: string; label: string; icon: React.FC<IconProps>; route: string };

const WORKFLOWS_NAV_ITEMS: NavItem[] = [
  { id: 'dialogs', label: 'common.dialogs', icon: FlowChartIcon, route: 'workflows/dialogs' },
  {
    id: 'intents',
    label: 'common.intents',
    icon: (props) => <LocateFixedIcon size={16} {...props} />,
    route: 'workflows/intents',
  },
  {
    id: 'entities',
    label: 'common.entities',
    icon: (props) => <CrosshairIcon size={16} {...props} />,
    route: 'workflows/entities',
  },
  {
    id: 'webhooks',
    label: 'common.webhooks',
    icon: (props) => <WebhookIcon size={16} {...props} />,
    route: 'workflows/webhooks',
  },
];

export const NavigationSidebar = () => {
  // Custom hooks
  const { t } = useTranslation();
  const location = useLocation();
  const isBelowNavBreakpoint = useBreakpoint('lg');

  // Redux selectors
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);

  // This sidebar never renders on a webhook's editor or wizard, so a trailing `webhooks` is the list
  const isWebhooksListingRoute = location.pathname.replace(/\/+$/, '').endsWith('/webhooks');

  // Local state
  const [open, setOpen] = useState(!isBelowNavBreakpoint);
  const [manuallyToggled, setManuallyToggled] = useState(false);

  // Handle responsive behavior for webhooks listing route
  useEffect(() => {
    if (isWebhooksListingRoute && !manuallyToggled) {
      setOpen(!isBelowNavBreakpoint);
    }
  }, [isBelowNavBreakpoint, isWebhooksListingRoute, manuallyToggled]);

  // Reset manual toggle flag when route changes
  useEffect(() => {
    setManuallyToggled(false);
  }, [location.pathname]);

  // Local variables
  const basePath = `/${slug}/ai-agents/${brainId}`;

  // Handlers
  const handleDrawerToggle = () => {
    setOpen((prev) => !prev);
    setManuallyToggled(true);
  };

  return (
    <nav className={cn(styles.nav, { [styles['nav--open']]: open })}>
      <div className={styles.toggle}>
        <Button variant="ghost-neutral" size="icon-sm" onClick={handleDrawerToggle}>
          <PanelLeftIcon size={16} />
        </Button>
      </div>

      <ul className={cn('h-full', { [styles.list]: true, [styles['list--collapsed']]: !open })}>
        {WORKFLOWS_NAV_ITEMS.map((item) => (
          <li key={item.id}>
            <NavLink
              className={({ isActive }) =>
                cn(styles.list__item, { [styles['list__item--active']]: isActive })
              }
              to={`${basePath}/${item.route}`}
            >
              <item.icon className="text-foreground-muted" />
              <span className="text-sm leading-5 font-normal">{t(item.label)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};
